// [PHASE 9B - NOV 20, 2025]: Added event subscriptions for reactive updates
import React, { useState, useEffect, useReducer } from 'react';
import { Package, Sparkles, Star } from 'lucide-react';
import { sourcesManager } from '../data/sourcesManager';
import {
  addItemToSource,
  addSpellToSource,
  addFeatToSource,
  addRaceToSource, // [CONTENT CREATORS - FEB 2026]
  addClassToSource, // [CONTENT CREATORS - FEB 2026]
  updateContentInSource
} from '../data/sources/initializeSources';
import { systemBus, EVENTS } from '../services/systemBus';
import { effectCalculator } from '../services/effectCalculator';
import MobileModal from './MobileModal';

const SourceContentDialog = ({ data, onSave, onCancel }) => {
  const { sourceId, category, contentKey, content, mode } = data;

  // ============================================================================
  // PHASE 9B - NOV 20, 2025: EVENT SUBSCRIPTIONS
  // ============================================================================
  // [PURPOSE]: Force re-render mechanism for reactive updates
  // [WHY]: Track source content events for custom content management
  // [INTEGRATION]: Follows pattern from Phase 6/9, similar to other dialog components
  // [NOTE]: Dialog manages form state, subscriptions provide logging and cache clearing
  const [, forceUpdate] = useReducer(x => x + 1, 0);

  // [PHASE 9B - NOV 20, 2025]: Subscribe to source content events for content editor updates
  // [PURPOSE]: Log source content changes (items, spells, feats added/updated)
  // [WHY]: Helps track when custom content is created or modified
  useEffect(() => {
    const handleSourceEvent = (eventName) => (data) => {
      effectCalculator.clearCache();
      forceUpdate();
    };

    // SOURCE_CONTENT_ADDED: Log when new content is added to a source
    const unsubscribeContentAdded = systemBus.on(EVENTS.SOURCE_CONTENT_ADDED, handleSourceEvent('SOURCE_CONTENT_ADDED'));

    // SOURCE_CONTENT_UPDATED: Log when existing content is updated
    const unsubscribeContentUpdated = systemBus.on(EVENTS.SOURCE_CONTENT_UPDATED, handleSourceEvent('SOURCE_CONTENT_UPDATED'));

    // SOURCE_CREATED: Log when new source is created (may affect available sources)
    const unsubscribeSourceCreated = systemBus.on(EVENTS.SOURCE_CREATED, handleSourceEvent('SOURCE_CREATED'));

    // Cleanup subscriptions on unmount
    return () => {
      unsubscribeContentAdded();
      unsubscribeContentUpdated();
      unsubscribeSourceCreated();
    };
  }, []); // Empty deps - subscribe once on mount

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    ...getDefaultFields(category)
  });

  useEffect(() => {
    if (mode === 'edit' && content) {
      setFormData({
        name: content.name || '',
        description: content.description || '',
        ...content
      });
    }
  }, [mode, content]);

  function getDefaultFields(category) {
    switch (category) {
      case 'items':
        return {
          baseType: '',
          category: 'items',
          weight: 0,
          value: 0,
          rarity: 'common',
          equipSlot: '',
          requiresAttunement: false
        };
      case 'spells':
        return {
          level: 0,
          school: 'evocation',
          castingTime: '1 action',
          range: 'Self',
          components: { v: false, s: false, m: false, materials: '' },
          duration: 'Instantaneous',
          classes: []
        };
      case 'feats':
        return {
          prerequisites: [],
          passiveEffects: [],
          activeAbilities: [],
          source: 'custom'
        };
      // [CONTENT CREATORS - FEB 2026]: Basic defaults for race/class editing via SourceContentDialog
      case 'races':
        return {
          size: 'Medium',
          speed: 30,
          abilityScoreIncrease: {},
          features: [],
          languages: ['Common'],
          darkvision: 0,
          isCustom: true
        };
      case 'classes':
        return {
          hitDice: '1d8',
          primaryAbility: 'Strength',
          savingThrows: [],
          proficiencies: { armor: [], weapons: [], tools: [], skills: { number: 2, options: [] } },
          features: {},
          isCustom: true
        };
      default:
        return {};
    }
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = () => {
    if (!formData.name) {
      alert('Name is required');
      return;
    }

    try {
      if (mode === 'edit') {
        // Update existing content
        updateContentInSource(sourceId, category, contentKey, formData);
      } else {
        // Add new content
        switch (category) {
          case 'items':
            addItemToSource(sourceId, formData);
            break;
          case 'spells':
            addSpellToSource(sourceId, formData);
            break;
          case 'feats':
            addFeatToSource(sourceId, formData);
            break;
          case 'races': // [CONTENT CREATORS - FEB 2026]
            addRaceToSource(sourceId, formData);
            break;
          case 'classes': // [CONTENT CREATORS - FEB 2026]
            addClassToSource(sourceId, formData);
            break;
          default:
            throw new Error('Unsupported category');
        }
      }
      onSave();
    } catch (e) {
      alert(`Failed to save: ${e.message}`);
    }
  };

  const renderItemFields = () => (
    <>
      <div>
        <label className="block text-sm text-gray-400 mb-1">Base Type</label>
        <input
          type="text"
          value={formData.baseType || ''}
          onChange={(e) => handleInputChange('baseType', e.target.value)}
          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
          placeholder="longsword"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm text-gray-400 mb-1">Weight (lbs)</label>
          <input
            type="number"
            value={formData.weight || 0}
            onChange={(e) => handleInputChange('weight', parseFloat(e.target.value) || 0)}
            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Value (gp)</label>
          <input
            type="number"
            value={formData.value || 0}
            onChange={(e) => handleInputChange('value', parseFloat(e.target.value) || 0)}
            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
          />
        </div>
      </div>
      <div>
        <label className="block text-sm text-gray-400 mb-1">Rarity</label>
        <select
          value={formData.rarity || 'common'}
          onChange={(e) => handleInputChange('rarity', e.target.value)}
          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
        >
          <option value="common">Common</option>
          <option value="uncommon">Uncommon</option>
          <option value="rare">Rare</option>
          <option value="very rare">Very Rare</option>
          <option value="legendary">Legendary</option>
        </select>
      </div>
      <div>
        <label className="flex items-center gap-2 text-sm text-gray-400">
          <input
            type="checkbox"
            checked={formData.requiresAttunement || false}
            onChange={(e) => handleInputChange('requiresAttunement', e.target.checked)}
            className="rounded bg-gray-600 border-gray-500"
          />
          Requires Attunement
        </label>
      </div>
    </>
  );

  const renderSpellFields = () => (
    <>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm text-gray-400 mb-1">Level</label>
          <select
            value={formData.level || 0}
            onChange={(e) => handleInputChange('level', parseInt(e.target.value))}
            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
          >
            {[...Array(10)].map((_, i) => (
              <option key={i} value={i}>{i === 0 ? 'Cantrip' : `Level ${i}`}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">School</label>
          <select
            value={formData.school || 'evocation'}
            onChange={(e) => handleInputChange('school', e.target.value)}
            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
          >
            <option value="abjuration">Abjuration</option>
            <option value="conjuration">Conjuration</option>
            <option value="divination">Divination</option>
            <option value="enchantment">Enchantment</option>
            <option value="evocation">Evocation</option>
            <option value="illusion">Illusion</option>
            <option value="necromancy">Necromancy</option>
            <option value="transmutation">Transmutation</option>
          </select>
        </div>
      </div>
      <div>
        <label className="block text-sm text-gray-400 mb-1">Casting Time</label>
        <input
          type="text"
          value={formData.castingTime || '1 action'}
          onChange={(e) => handleInputChange('castingTime', e.target.value)}
          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
          placeholder="1 action"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm text-gray-400 mb-1">Range</label>
          <input
            type="text"
            value={formData.range || 'Self'}
            onChange={(e) => handleInputChange('range', e.target.value)}
            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
            placeholder="60 feet"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Duration</label>
          <input
            type="text"
            value={formData.duration || 'Instantaneous'}
            onChange={(e) => handleInputChange('duration', e.target.value)}
            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
            placeholder="Concentration, up to 1 minute"
          />
        </div>
      </div>
    </>
  );

  const renderFeatFields = () => (
    <>
      <div>
        <label className="block text-sm text-gray-400 mb-1">Prerequisites (comma-separated)</label>
        <input
          type="text"
          value={Array.isArray(formData.prerequisites) ? formData.prerequisites.join(', ') : ''}
          onChange={(e) => handleInputChange('prerequisites', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
          placeholder="Strength 13 or higher"
        />
      </div>
      <div>
        <label className="block text-sm text-gray-400 mb-1">Source</label>
        <input
          type="text"
          value={formData.source || 'custom'}
          onChange={(e) => handleInputChange('source', e.target.value)}
          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
          placeholder="Custom"
        />
      </div>
    </>
  );

  const getIcon = () => {
    switch (category) {
      case 'items': return <Package className="w-5 h-5" />;
      case 'spells': return <Sparkles className="w-5 h-5" />;
      case 'feats': return <Star className="w-5 h-5" />;
      default: return <Package className="w-5 h-5" />;
    }
  };

  return (
    <MobileModal
      isOpen={true}
      onClose={onCancel}
      title={
        <span className="flex items-center gap-2">
          {getIcon()}
          {mode === 'edit' ? 'Edit' : 'Add'} {category.slice(0, -1)}
        </span>
      }
      onSave={handleSave}
      saveLabel="Save"
    >
      <div className="space-y-4">
        {/* Common fields */}
        <div>
          <label className="block text-sm text-gray-400 mb-1">Name *</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
            placeholder="Enter name..."
          />
        </div>

        <div>
          <label className="block text-sm text-gray-400 mb-1">Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
            rows="3"
            placeholder="Enter description..."
          />
        </div>

        {/* Category-specific fields */}
        {category === 'items' && renderItemFields()}
        {category === 'spells' && renderSpellFields()}
        {category === 'feats' && renderFeatFields()}
      </div>
    </MobileModal>
  );
};

export default SourceContentDialog;
